import { t } from '@transifex/native';

import * as flashActions from 'pkg/actions/flashes';

import * as endpoints from 'pkg/api/endpoints/auto';
import type {
	AttendanceStatuses,
	EventUser,
	EventUserFlags,
	EventUserRole,
	EventUserStatus,
} from 'pkg/api/models/event_user';
import * as sdk from 'pkg/core/sdk';
import { crash } from 'pkg/errors/errors';

interface InviteUsersPayload {
	sendInvites: boolean;
	users: number[];
	organizer?: {
		[key: number]: boolean;
	};
}

interface InviteUsersOptions {
	// isScheduled tells the toast that invites will be deferred by the
	// scheduled-invite job rather than sent immediately, so users see
	// "Invites scheduled" instead of the misleading "Invites sent".
	isScheduled?: boolean;
}

export const inviteEventUsers = async (
	organizationId: number,
	eventId: number,
	payload: InviteUsersPayload,
	options: InviteUsersOptions = {}
) => {
	const req = await sdk.post(
		endpoints.Events.AddUsers(organizationId, eventId),
		{},
		payload
	);
	if (!req.ok) {
		const response = await req.json().catch(() => ({}));

		flashActions.show(
			{
				title: t('Could not add users'),
				message: response?.error,
			},
			req.status
		);

		return;
	}

	const resp: EventUser[] = await req.json();

	const message = t('Added {num} users to the event', {
		num: payload.users.length,
	});

	if (!payload.sendInvites) {
		flashActions.show({ title: t('Added users'), message });
	} else if (options.isScheduled) {
		flashActions.show({ title: t('Invites scheduled'), message });
	} else {
		flashActions.show({ title: t('Invites sent'), message });
	}

	return resp || [];
};

export const resendInvites = async (
	organizationId: number,
	eventId: number
) => {
	const req = await sdk.post(
		endpoints.Events.ResendInvites(organizationId, eventId)
	);
	if (!req.ok) {
		return;
	}

	flashActions.show({
		title: t('Invites sent'),
	});
};

export const deleteEventUsers = async (
	organizationId: number,
	eventId: number,
	userIds: number[]
) => {
	const req = await sdk.destroy(
		endpoints.Events.RemoveUsers(organizationId, eventId),
		{},
		{ users: userIds }
	);

	if (req.ok) {
		return true;
	}

	return false;
};

interface PatcheventUsersPayload {
	users: {
		userId: number;
		status?: EventUserStatus;
		comment?: string;
		attendanceStatus?: AttendanceStatuses;
		role?: EventUserRole;
		flags?: EventUserFlags[];
	}[];
	physicalStrain?: string;
}

export const patchEventUsers = async (
	organizationId: number,
	eventId: number,
	payload: PatcheventUsersPayload
) => {
	const req = await sdk.patch(
		endpoints.Events.UpdateUsers(organizationId, eventId),
		{},
		payload
	);

	if (req.ok) {
		return true;
	}

	const error = crash();

	flashActions.show({
		title: error.title,
		message: error.description,
	});

	return false;
};
